import type { ComponentProps } from "react";
import type { DateRange } from "react-day-picker";
import { calendarDateKey } from "@/shared/utils/calendarDate";
import { cn } from "@/shared/utils/cn";
import { Calendar, CalendarDayButton } from "../shadcn/Calendar";
import "./dateTimePicker.css";

interface DateCalendarProps {
  today: Date;
  selectedDay?: Date;
  selectedRange?: DateRange;
  onDateSelect?: (date: Date) => void;
  month?: Date;
  onMonthChange?: (month: Date) => void;
  disabled?: ComponentProps<typeof Calendar>["disabled"];
  dayButton?: typeof CalendarDayButton;
  className?: string;
}

export function DateCalendar({
  today, selectedDay, selectedRange, onDateSelect, month, onMonthChange, disabled, dayButton, className,
}: DateCalendarProps) {
  return (
    <Calendar
      {...(selectedRange ? {
        mode: "range" as const,
        selected: selectedRange,
        onSelect: (_range: DateRange | undefined, date: Date) => onDateSelect?.(date),
      } : {
        mode: "single" as const,
        selected: selectedDay,
        onSelect: (date: Date | undefined) => { if (date) onDateSelect?.(date); },
      })}
      defaultMonth={selectedDay ?? today}
      month={month}
      onMonthChange={onMonthChange}
      today={today}
      disabled={disabled}
      numberOfMonths={1}
      startMonth={new Date(Math.min(today.getFullYear() - 10, selectedDay?.getFullYear() ?? today.getFullYear()), 0, 1)}
      endMonth={new Date(Math.max(today.getFullYear() + 10, selectedDay?.getFullYear() ?? today.getFullYear()), 11, 31)}
      showOutsideDays
      captionLayout="dropdown"
      className={cn("date-calendar w-full rounded-[20px] bg-card p-3 [--cell-size:clamp(2.75rem,8vw,3.25rem)]", className)}
      formatters={{ formatMonthDropdown: (date) => date.toLocaleString(undefined, { month: "long" }) }}
      components={{
        DayButton: dayButton ?? DateCalendarDayButton,
      }}
    />
  );
}

function DateCalendarDayButton({ children, ...props }: ComponentProps<typeof CalendarDayButton>) {
  return (
    <CalendarDayButton {...props}>
      <span data-date={calendarDateKey(props.day.date)} className="relative flex min-h-11 w-full items-center justify-center">{children}</span>
    </CalendarDayButton>
  );
}
